/* eslint-disable max-lines */
import { readCookie } from "common/utils/cookie";
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import { parseArtworkUrls, parseJsonArray } from "common/utils/json";
import pinia from "common/utils/pinia";
import { waitForWindowVariable } from "common/utils/ui/wait_for";
import clickOutside from "components/jiffy/vue-cart/filters/clickOutside";
import { track as mixpanelTrack } from "jiffy-analytics";
import { createApp, nextTick } from "vue";
import { initAllBanners } from "components/jiffy/promotion-banner/promotion-banner";
import i18n from "../vue-cart/vue-i18n";
import { GANG_DEFAULT_VALUES, INCHES_PER_FOOT } from "./common/constants";
import { trackPDPPageVisit } from "./dtf-ga-tracker";
import ProductDetailsDtf from "./ProductDetailsDtf.vue";
import { useDtfStore } from "./store";
import { isIncompatibleFileFormat } from "./utils";
import "./vue-product-details-dtf.scss";

const track = (event, payload) => {
  if (window.mixpanel) {
    mixpanelTrack(event, payload);
  } else {
    waitForWindowVariable("mixpanel", () => mixpanelTrack(event, payload));
  }
};

/* eslint-disable max-lines-per-function */
const initProductDetailsDtf = async () => {
  const ProductDetailsDtfWrapper = createApp({});
  ProductDetailsDtfWrapper.use(pinia);
  const $wrapper = document.getElementById("vue-product-details-dtf");

  const appConfig = JSON.parse($wrapper.dataset.appConfig);

  /* eslint-disable camelcase */
  const { variant, favorites, experiments, cached_artwork_prices } = appConfig;
  /* eslint-enable camelcase */

  const product = objNamesToCamelCase(appConfig.product);
  const config = objNamesToCamelCase(appConfig.config_data);
  const spangleConfig = objNamesToCamelCase(appConfig.spangle_data);
  const gangSheetConfig = appConfig.gang_sheet_data;

  const urlParams = new URLSearchParams(window.location.search);
  const rawArtworkIds = urlParams.get("uploaded_artwork_ids") || readCookie("uploaded_artwork_ids");
  const buyAsStickerArtworkIds = urlParams.get("buy_as_sticker_artwork_ids");
  const artworkWidth = urlParams.get("artwork_width");
  const artworkHeight = urlParams.get("artwork_height");
  const artworkPaddingSize = urlParams.get("artwork_padding_size");
  const artworkBgColor = urlParams.get("artwork_bg_color");
  const artworkColor = urlParams.get("artwork_color");
  const artworkShape = urlParams.get("artwork_shape");
  const processArtworkParam = urlParams.get("process_artwork");
  const processArtwork = processArtworkParam !== null ? processArtworkParam === "true" : true;
  const urlPreCut = urlParams.get("pre_cut");
  const orderNumber = urlParams.get("buy_all_from_order");
  const fileContentType = urlParams.get("file_content_type");
  const sourceParam = urlParams.get("source");
  const userType = readCookie("name") ? "signed_in" : "guest_user";
  const letterNumberHeight = urlParams.get("height");
  const letterNumberStyle = urlParams.get("style");
  const letterNumberColor = urlParams.get("color");
  const letterNumberMaxLength = urlParams.get("max_length");
  const letterNumberFont = urlParams.get("font");
  const rawArtworkUrls = urlParams.get("artwork_urls");
  const designId = urlParams.get("design_id");

  // New enhancement parameters
  const enhancementsStarted = urlParams.get("enhancements_started") === "true";
  const dtfChannelUuid = urlParams.get("dtf_channel_uuid");

  let options = {};

  ProductDetailsDtfWrapper.component("ProductDetailsDtf", ProductDetailsDtf);
  ProductDetailsDtfWrapper.use(i18n);
  ProductDetailsDtfWrapper.directive("click-outside", clickOutside());

  const store = useDtfStore();

  store.setShowCTA(config.showCta);
  store.setIsMobile(config.mobile);
  store.setPreCutDuration(config.preCutDuration);
  store.setPreCutUpsellThreshold(config.preCutUpsellThreshold);
  store.setAllowForceBgRemove(config.allowForceBgRemove);
  store.setCanvaDtfEnabled(config.canvaDtfEnabled);
  store.setForcePdpTransfersRedraw(config.forcePdpTransfersRedraw);
  store.setPinBasedRedrawEnabled(config.pinBasedRedrawEnabled);
  store.setEnableRedraw(config.enableRedraw);
  store.setCanvaReviewer(config.canvaReviewer);
  store.setShowMultiSize(config.showMultiSize);
  store.setPaidExpertEditAddonEnabled(config.paidExpertEditAddonEnabled);
  store.setGangSheetCatalogNumbers(config.gangSheetCatalogNumbers || []);
  store.setEnableMobileFooterCTA(config.enableMobileFooterCta);
  store.setCustomerReviewOnRedraws(config.customerReviewOnRedraws);
  store.setCustomerReviewOnTransferRedraws(config.customerReviewOnTransferRedraws);
  store.setPinSessionRecording(config.pinSessionRecording);
  store.setPinSessionForceMixpanelRecording(config.pinSessionForceMixpanelRecording);
  store.setDtfWorkerEnhancement(config.dtfWorkerEnhancement);
  store.setDtfBackendTriggeredEnhancement(config.dtfBackendTriggeredEnhancement);
  store.setTransferPdpBackButtonDetection(config.transferPdpBackButtonDetection);

  store.setPreCutPrice(product.preCutPrice);
  store.setPreCutEnabled(product.preCutEnabled);
  store.setProductId(product.id);
  store.setProductSlug(product.slug);
  store.setPdpType(product.dtfPdpType);
  store.setCatalogNumber(product.catalogNumber);
  store.setPrintAsIs(product.printAsIs);
  store.setNeckLabel(product.neckLabel);
  store.setSticker(product.sticker);
  store.setDtfMaxWidth(Number(product.uploadMaxWidth));
  store.setDtfMaxHeight(Number(product.uploadMaxHeight));
  store.setDtfMinWidth(Number(product.uploadMinWidth));
  store.setDtfMinHeight(Number(product.uploadMinHeight));
  store.setPrintingServiceName(product.printingServiceName);
  store.setIsSpangle(product.isSpangle);
  store.setIsProColor(product.isProColor);
  store.setIsLetters(product.isLetters);
  store.setBusinessLine(product.businessLine);
  store.setIsPuff(product.isPuff);
  store.setIsSticker(product.isSticker);
  store.setIsDieCutSticker(product.isDieCutSticker);
  store.setIsStickerSample(product.isStickerSample);
  store.setIsJerseyNumbers(product.isJerseyNumbers);
  store.setDtfCategory(product.dtfCategory);
  store.setIsGoldFoil(product.isGoldFoil);
  store.setReorderEnabled(product.reorderEnabled);
  store.setIsDtfSheet(product.isDtfSheet);

  store.setSpangleAlgorithms(spangleConfig.spangleVersionIdentifiers);
  store.setSpangleColors(spangleConfig.spangleColors);
  /* eslint-disable camelcase, no-undef */
  store.setGangSheetBuilder(typeof gs_builder !== "undefined" ? gs_builder : null);
  store.setGangSheetBuilderShopId(gangSheetConfig.gang_sheet_builder_shop_id);
  store.setShowGangSheetBuilderFeature(gangSheetConfig.show_gang_sheet_builder_feature);
  store.setGangSheetBuilderSizes(gangSheetConfig.gang_sheet_builder_sizes);

  store.setVariantId(variant.variant_id);
  store.setFavorites(favorites);
  store.setExperiments(experiments);
  store.setUserType(userType);
  store.setCachedArtworkPrices(JSON.parse(cached_artwork_prices));
  store.setKlaviyoSmsTransactionalSubscribed(config.klaviyoSmsTransactionalSubscribed);
  store.setSmsTransactionalSubscriptionPdpEnabled(config.smsTransactionalSubscriptionPdpEnabled);
  store.setTransferPdpInactivityDetection(config.transferPdpInactivityDetection);
  store.setBogoEligibility(config.bogoEligibility || { visible: false, reason: null });
  store.setBogoMaxDimensionLabel(config.bogoMaxDimensionLabel || '12"x12"');
  store.setPromotionResolution(config.promotionResolution || { winner: "none" });

  const applyPromotionResolutionFromApi = () => {
    if (document.pdpDynamicData?.promotion_resolution) {
      store.setPromotionResolution(
        objNamesToCamelCase(document.pdpDynamicData.promotion_resolution)
      );
    }
    import("components/jiffy/reseller-pricing-modal/reseller-pricing-modal").then(module => {
      module.default();
    });
    import("components/jiffy/promotion-banner/promotion-banner").then(module => {
      module.initAllBanners();
    });
  };
  if (document.pdpDynamicData?.promotion_resolution) {
    applyPromotionResolutionFromApi();
  }
  document.addEventListener("dynamicDataLoaded", applyPromotionResolutionFromApi);

  if (product.isGangSheetBuilder) {
    const variants = gangSheetConfig.gang_sheet_builder_variants;
    let defaultVariant = variants[Object.keys(variants)[0]];

    if (urlParams.get("v")) {
      const selectedVariant = Object.values(variants).find(
        v => v.catalog_number === urlParams.get("v")
      );

      defaultVariant = selectedVariant || defaultVariant;
    }

    const settings = {
      height: artworkHeight ? Number(artworkHeight) : GANG_DEFAULT_VALUES.height * INCHES_PER_FOOT,
      variantId: defaultVariant.variant_id
    };

    store.updateGangSheetBuilderSettings(settings);
    store.setIsGangSheetBuilder(product.isGangSheetBuilder);
    store.setGangSheetBuilderVariants(variants);
    store.setVariantId(defaultVariant.variant_id);
    store.setProductSlug(defaultVariant.product_slug);
    store.setGangSheetBuilderCarousels(gangSheetConfig.gang_sheet_builder_carousels);
  }

  if (buyAsStickerArtworkIds) {
    const artworkIds = parseJsonArray(decodeURIComponent(buyAsStickerArtworkIds));
    if (artworkIds && artworkIds.length) {
      store.buyAsStickerById({ artworkIds });
    }
  }

  if (rawArtworkIds) {
    const artworkIds = parseJsonArray(decodeURIComponent(rawArtworkIds)) || [];
    const parsedUrls = parseArtworkUrls(rawArtworkUrls);
    const artworkUrls = parsedUrls?.urlsObject || {};

    artworkIds.forEach((artworkId, index) => {
      store.setEmptyArtwork(artworkId);

      // Skip incompatible formats (HEIC/SVG)
      const isIncompatibleFormat = isIncompatibleFileFormat(fileContentType);

      const artworkData = {
        id: artworkId,
        progress: 25,
        progressMessage: "Processing file",
        processTimeStamp: Date.now(),
        width: artworkWidth,
        height: artworkHeight,
        originalIndex: index
      };

      // Only set url for compatible formats
      if (!isIncompatibleFormat && artworkUrls[artworkId]) {
        artworkData.url = artworkUrls[artworkId];
        artworkData.isFile = true;
      }

      store.updateArtworkValuesById(artworkData);
    });
    options = {
      artworkIds,
      artworkWidth,
      artworkHeight,
      processArtwork,
      urlPreCut,
      fileContentType,
      artworkPaddingSize,
      artworkBgColor,
      artworkColor,
      artworkShape
    };

    store.setActionCableOptions(options);
  }

  // Store the AI Studio upload data for later processing after Vue app is mounted
  let aiStudioUploadData = null;
  if (rawArtworkUrls && fileContentType && !enhancementsStarted) {
    aiStudioUploadData = {
      artworkUrls: rawArtworkUrls,
      fileContentType
    };
  }

  if (product.sticker && sourceParam === "gangsheet") {
    if (userType === "signed_in") {
      store.setShowArtworkHistoryModal(true);
      store.setStickerQrScanned(true);
    }

    track("sticker_qr_scanned_on_gangsheets", {});
  }

  if (product.isJerseyNumbers && letterNumberHeight && letterNumberStyle && letterNumberColor) {
    store.setLoadFromConfig(false);
    store.updateArtworkValues({
      index: 0,
      height: Number(letterNumberHeight),
      color: letterNumberColor,
      style: letterNumberStyle
    });
    const isPreCut = urlPreCut === "true";
    store.setPreCut(isPreCut);
    store.setEnablePreCut();
  }

  if (
    product.isLetters &&
    letterNumberColor &&
    letterNumberStyle &&
    letterNumberMaxLength &&
    letterNumberHeight &&
    letterNumberFont
  ) {
    store.setLoadFromConfig(false);
    store.updateArtworkValues({
      index: 0,
      height: Number(letterNumberHeight),
      color: letterNumberColor,
      style: letterNumberStyle,
      maxLength: Number(letterNumberMaxLength),
      font: letterNumberFont
    });
    const isPreCut = urlPreCut === "true";
    store.setPreCut(isPreCut);
    store.setEnablePreCut();
  }

  if (orderNumber) {
    store.buyArtworksAgainFromOrder(orderNumber);
  }

  if (designId) {
    store.setPreloadedDesignId(designId);
  }

  // Disable scroll restoration to improve CLS on pages where vue product details is used
  if (window.history.scrollRestoration) {
    window.history.scrollRestoration = "manual";
  }

  ProductDetailsDtfWrapper.mount("#vue-product-details-dtf");

  // Hide skeleton loader after Vue component is mounted and rendered
  // Use nextTick to wait for Vue's DOM updates, then requestAnimationFrame for paint
  nextTick(() => {
    const $skeleton = document.querySelector(".js-dtf-skeleton");
    requestAnimationFrame(() => {
      $skeleton?.classList.add("hidden");
    });

    initAllBanners();
  });

  // Handle enhancement loading flow when enhancements_started=true
  if (enhancementsStarted && dtfChannelUuid && rawArtworkUrls) {
    store.loadEnhancementArtwork({
      enhancementsStarted,
      dtfChannelUuid,
      rawArtworkUrls,
      artworkWidth,
      artworkHeight
    });
  }
  // Handle AI Studio upload after Vue app is mounted (only if not enhancement flow)
  else if (aiStudioUploadData && !rawArtworkIds) {
    (async () => {
      try {
        const parsedUrls = parseArtworkUrls(aiStudioUploadData.artworkUrls);
        if (!parsedUrls) {
          return;
        }
        const { imageUrl, artworkIds, lastArtworkId } = parsedUrls;

        if (artworkIds.length > 0) {
          // Download the file and use the proper upload flow
          const { getFileFromUrl } = await import("common/utils/file");
          const file = await getFileFromUrl(
            imageUrl,
            `artwork_image-${Date.now()}`,
            aiStudioUploadData.fileContentType
          );

          // Use the store's upload method to properly handle the file
          store.addFilesToQueue({
            files: [file],
            isDropEvent: false,
            existingArtworkId: lastArtworkId
          });

          // Clean up URL parameters
          const newUrl = new URL(window.location);
          newUrl.searchParams.delete("artwork_urls");
          newUrl.searchParams.delete("file_content_type");
          newUrl.searchParams.delete("side");
          window.history.replaceState({}, "", newUrl.toString());
        }
      } catch (error) {
        console.error("Error during AI Studio auto upload:", error);
      }
    })();
  }

  trackPDPPageVisit();
};
/* eslint-enable max-lines-per-function */
/* eslint-enable max-lines */

export default initProductDetailsDtf;
